import {RefObject, useContext, useEffect, useRef} from 'react';
import {MessageThreadSummary, MessageThread} from '@/api/dto/message';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {log} from '@/helpers/logger';
import Helpers from '@/helpers/helpers';

// Delay in milliseconds before triggering API call, message needs to be present in viewport for that long
export const UNREAD_TRIGGER_DELAY = 1000;

// Offset from top part of message that triggers API call, for now - just height of an editor
export const UNREAD_TRIGGER_OFFSET = 52;

type Options = {
  messageThreadSummary: RefObject<MessageThreadSummary | null>;
  messageThread?: MessageThread;
  isOpen: boolean;
  isReady: boolean;
  isActive?: boolean;
  content: RefObject<HTMLElement | null>;
}

let lastMessageId = 0;
let highestMessageId = 0;

export function useLastSeenMessage({ messageThreadSummary, messageThread, isOpen, isReady, content }: Options) {
  const { carecoApi } = useContext(CarecoApiContext);

  const timeout = useRef<ReturnType<typeof setTimeout>>(null);
  const scrollParent = useRef<HTMLElement>(null);
  const lastSeenMessageId = useRef<number>(null);

  useEffect(() => {
    if (!isOpen || !isReady) {
      return;
    }

    lastMessageId = 0;
    highestMessageId = messageThreadSummary?.current?.lastSeenMessage?.id ?? 0;

    scrollParent.current = Helpers.findScrollParent(content.current);
    scrollParent.current?.addEventListener('scroll', handleTopmostMessage, { passive: true });
    scrollParent.current?.addEventListener('resize', handleTopmostMessage, { passive: true });

    // This timeout makes sure messageThreadSummary is up-to-date
    setTimeout(() => {
      scrollToLastSeenMessage();
      void handleTopmostMessage();
    });

    return () => {
      clearTimeout(timeout.current ?? undefined);
      scrollParent.current?.removeEventListener('scroll', handleTopmostMessage);
      scrollParent.current?.removeEventListener('resize', handleTopmostMessage);
    };
  }, [isOpen, isReady]);

  const handleTopmostMessage = () => {
    return new Promise((resolve) => {
      const topmostMessageId = findTopmostMessageId();

      if (topmostMessageId !== lastMessageId) {
        clearTimeout(timeout.current ?? undefined);
        timeout.current = null;
      }

      lastMessageId = topmostMessageId;

      if (!timeout.current && topmostMessageId > highestMessageId) {
        timeout.current = setTimeout(() => {
          highestMessageId = topmostMessageId;
          handleReadMessage(topmostMessageId).then(resolve);
        }, UNREAD_TRIGGER_DELAY);
      }
    });
  };

  const handleReadMessage = async (messageId: number) => {
    if (!messageThreadSummary?.current || !messageId) {
      return;
    }

    return carecoApi?.readMessage(messageThreadSummary.current.id, messageId)
      .then(() => {
        lastSeenMessageId.current = messageId;
      })
      .catch((error) => {
        log('error', 'Failed to mark message as read:', error);
      });
  };

  const findTopmostMessageId = () => {
    const topmostMessage = Array.from(document.querySelectorAll<HTMLElement>('[data-message-id]'))
      .find((message) =>
        message.getBoundingClientRect().top
        - (scrollParent.current?.getBoundingClientRect().top ?? 0)
        - UNREAD_TRIGGER_OFFSET > 0);

    return Number(topmostMessage?.getAttribute('data-message-id') ?? 0);
  };

  const scrollToLastSeenMessage = () => {
    if (messageThreadSummary?.current?.lastSeenMessage?.id === messageThread?.messages[0]?.id) {
      return;
    }

    const lastSeenMessage = document.querySelector('[data-last-seen-message]');
    lastSeenMessage?.scrollIntoView();
  };

  return {
    scrollParent,
    lastSeenMessageId,
    handleTopmostMessage,
  };
}
